/**
 * useSprintRetro — the React binding for the Sprint Retro page (/retro).
 *
 * Strict layering: the page calls THIS hook; the hook calls
 * {@link SprintRetroStore}; the store calls the api facade. The page never
 * imports the store or the api directly. Mirrors
 * {@link ../../preplanning/usePrePlanning}.
 *
 * Exposes the retro summaries (so the picker can flag which sprints already have
 * a retro), a {@link UseSprintRetroResult.loadForSprint} that hydrates (or
 * returns undefined for) a sprint's retro on demand, and a
 * {@link UseSprintRetroResult.save} upsert + {@link UseSprintRetroResult.remove}
 * soft-delete. Tenant scoping is automatic (the store routes by the active team
 * bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import { SprintRetroStore } from './sprintRetroStore';
import type {
  SprintRetro,
  SprintRetroDraft,
  SprintRetroSummary,
} from './types';

export interface UseSprintRetroResult {
  /** All non-deleted retro summaries (one per sprint that has a retro). */
  retros: SprintRetroSummary[];
  /** The alias of the current user (kudos `from` default). */
  currentAlias: string;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Hydrates a sprint's retro, or undefined when the sprint has none yet. */
  loadForSprint: (sprintId: string) => Promise<SprintRetro | undefined>;
  /** Upserts a sprint's retro from a draft; refreshes the summaries. */
  save: (draft: SprintRetroDraft) => Promise<SprintRetro>;
  /** Soft-deletes a sprint's retro; refreshes the summaries. */
  remove: (sprintId: string) => Promise<void>;
}

export function useSprintRetro(): UseSprintRetroResult {
  const api = useApi();
  const store = useMemo(() => new SprintRetroStore(api), [api]);

  const [retros, setRetros] = useState<SprintRetroSummary[]>([]);
  const [currentAlias, setCurrentAlias] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, identity] = await Promise.all([
        store.listSummaries(),
        api.identityProvider.getIdentity(),
      ]);
      setRetros(rows);
      setCurrentAlias(identity.alias);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load retros.');
    } finally {
      setLoading(false);
    }
  }, [store, api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const loadForSprint = useCallback(
    (sprintId: string) => store.getBySprint(sprintId),
    [store],
  );

  const save = useCallback(
    async (draft: SprintRetroDraft) => {
      const saved = await store.saveDraft(draft);
      await refresh();
      return saved;
    },
    [store, refresh],
  );

  const remove = useCallback(
    async (sprintId: string) => {
      await store.softDelete(sprintId);
      await refresh();
    },
    [store, refresh],
  );

  return {
    retros,
    currentAlias,
    loading,
    error,
    refresh,
    loadForSprint,
    save,
    remove,
  };
}
